import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import type { Tables } from '@pps/supabase';
import { cleanPayload, extractEdgeError } from '@pps/supabase/payload';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';

type ClubMemberRow = Tables<'club_members'>;

export type ClubMemberWithProfile = ClubMemberRow & {
  profiles: {
    full_name: string | null;
    phone: string | null;
    avatar_url: string | null;
  } | null;
};

const MEMBERS_KEYS = [['club-members'], ['pending-members-count']] as const;

/**
 * Lista club_members com profile aninhado (full_name/phone/avatar_url).
 * IR-CI-008: extração mecânica de `pages/Members.tsx`.
 *
 * Pattern de nested-select alinhado com `useStudents` (StudentWithRelations):
 * tipo estendido `& { profiles: { ... } | null }` casa com o shape devolvido
 * pelo Supabase quando se usa `select('*, profiles:player_id(...)')`.
 */
export function useClubMembers(clubId: string | undefined) {
  return useQuery({
    queryKey: ['club-members', clubId],
    enabled: !!clubId,
    queryFn: async (): Promise<ClubMemberWithProfile[]> => {
      if (!clubId) return [];
      const { data, error } = await supabase
        .from('club_members')
        .select('*, profiles:player_id(full_name, phone, avatar_url)')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClubMemberWithProfile[];
    },
  });
}

/**
 * Conta members com status='pending' para um clube. Versão `count` head-only
 * (sem trazer rows). Usado pelo badge do AppSidebar. IR-CI-008.
 */
export function usePendingMembersCount(clubId: string | undefined) {
  return useQuery({
    queryKey: ['pending-members-count', clubId],
    enabled: !!clubId,
    queryFn: async (): Promise<number> => {
      if (!clubId) return 0;
      const { count, error } = await supabase
        .from('club_members')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .eq('status', 'pending');
      // Mantém semântica do legado: erros retornam 0 silenciosamente (UX
      // de badge tolera ausência; não vale invalidar a UI por isso).
      if (error) return 0;
      return count ?? 0;
    },
  });
}

export interface UpdateMemberStatusInput {
  id: string;
  status: 'approved' | 'rejected';
  reviewerUserId: string | undefined;
}

/**
 * Atualiza status (approved/rejected) de um club_member. IR-CI-008.
 */
export function useUpdateMemberStatus() {
  return useDomainMutation<void, Error, UpdateMemberStatusInput>({
    mutationFn: async ({ id, status, reviewerUserId }) => {
      const { error } = await supabase
        .from('club_members')
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: reviewerUserId,
        })
        .eq('id', id);
      if (error) throw error;
    },
    invalidates: MEMBERS_KEYS,
    toast: {
      success: i18n.t('members.statusUpdated'),
      error: i18n.t('common.error'),
    },
    tags: { feature: 'crm', action: 'update_member_status' },
  });
}

export interface InviteMemberInput {
  clubId: string | undefined;
  name: string;
  email: string;
  phone: string;
}

/**
 * Convida um membro via Edge Function `invite-member`. IR-CI-008.
 *
 * `extractEdgeError` preserva mensagens localizadas (rate_limited, forbidden,
 * external_service) — o caller monta a mensagem traduzida e lança Error
 * com o `message` final; wrapper apenas renderiza no toast de erro default.
 *
 * Nota: a regra `no-supabase-in-ui` não cobre `supabase.functions.invoke`
 * (apenas `.from(...)`), então o invite em si poderia ficar inline. Move-se
 * junto pelo princípio de "todo write de domínio mora num hook" — facilita
 * teste, mock e reuso. Mesmo padrão de `useNfse`/`useWhatsApp`.
 */
export function useInviteMember() {
  return useDomainMutation<unknown, Error, InviteMemberInput>({
    mutationFn: async ({ clubId, name, email, phone }) => {
      const { data, error } = await supabase.functions.invoke('invite-member', {
        body: cleanPayload({ club_id: clubId, name, email, phone }),
      });
      if (error) {
        const code = await extractEdgeError(error);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        if (code === 'external_service') throw new Error(i18n.t('common.serviceError'));
        throw error;
      }
      // `supabase.functions.invoke` retorna `data: unknown`; verifica shape
      // antes de acessar campo `error` (preserva semântica do legado em
      // `pages/Members.tsx`, que tratava erro do payload da function como
      // override do erro de transport).
      const dataObj = data as { error?: string } | null | undefined;
      if (dataObj?.error) {
        const code = await extractEdgeError(dataObj);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        throw new Error(dataObj.error);
      }
      return data;
    },
    invalidates: MEMBERS_KEYS,
    toast: {
      success: i18n.t('members.inviteSuccess'),
      error: i18n.t('members.inviteError'),
    },
    tags: { feature: 'crm', action: 'invite_member' },
  });
}
